import type { BenchmarkToolSurface, BenchmarkTraceEventData } from './types';

/**
 * Strips the wrapper the sandbox puts around every shell command, turning
 * `/usr/bin/bash -lc 'twg work query --since 14d'` into `twg work query
 * --since 14d`.
 *
 * The leading path is matched as "any first token ending in `sh`" rather than
 * as the specific interpreters seen so far, so a change of shell or of its
 * location degrades to showing the wrapped form instead of hiding the command.
 * The quoted body is matched greedily to the closing quote at the end of the
 * string, because real commands nest the other quote character inside.
 */
const SHELL_WRAPPER_PATTERN = /^\S*sh\s+-[a-z]*c\s+(['"])([\s\S]*)\1$/;

/**
 * Whether a command drives the Teamwork Graph CLI.
 *
 * Matches the audited wrapper, an absolute path to it, and the real binary
 * behind it. The last of those is the case that raises a wrapper-bypass
 * integrity violation, so classifying it as a plain shell command would
 * mislabel precisely the invocation the violation is complaining about.
 */
const TWG_COMMAND_PATTERN = /(?:^|\s|\/)twg(?:-real)?(?:\s|$)/;

/**
 * Content the producer emits as its final `trace` event once a diagnostic
 * journal hits its 14 MiB total or 24 KiB per-event ceiling. Tracing stops
 * silently after it, so the transcript from that point on is incomplete.
 */
const CAPTURE_TRUNCATED_CONTENT = '[diagnostic_capture_truncated]';

/** Emitted in place of the payload when the service's redaction pass threw. */
const REDACTION_FAILED_CONTENT = '[diagnostic_redaction_failed]';

export const unwrapShellCommand = (command: string): string => {
	const wrapper = SHELL_WRAPPER_PATTERN.exec(command);

	return wrapper ? wrapper[2] : command;
};

export type BenchmarkInvocationKind = 'command' | 'mcp';

/**
 * One renderable fact about a tool invocation, derived from a Codex transcript
 * record.
 *
 * This type is deliberately narrow, and that is the point. `trace` payloads
 * carry MCP arguments, tool results and captured stdout, and the service's
 * redaction targets credential-shaped keys rather than personal information: a
 * verified run passed an account holder's email, job title, department,
 * manager account id and time zone through a tool result. Mapping to this
 * shape inside this module means those payloads are read but never handed
 * onwards, so no consumer can render them by accident.
 */
export type BenchmarkInvocationDelta = {
	phase: 'started' | 'completed';
	/**
	 * The Codex `item.id`. This is the reliable pairing key that `tool` events
	 * lack, and the reason the timeline is built from `trace` at all.
	 */
	invocationId: string;
	kind: BenchmarkInvocationKind;
	surface: BenchmarkToolSurface;
	/** The executed command, or `server.tool` for an MCP call. */
	label: string;
	failed: boolean;
	exitCode: number | null;
};

export type BenchmarkTraceOutcome =
	| { kind: 'invocation'; delta: BenchmarkInvocationDelta }
	/**
	 * The transcript stopped being usable from here on, either because a
	 * capture ceiling was reached or because the service's redaction pass
	 * failed. Both leave the row list incomplete, and neither is recoverable.
	 */
	| { kind: 'captureIncomplete'; reason: 'limit' | 'redactionFailed' }
	| { kind: 'ignored' };

/**
 * Partially received multi-chunk trace payloads, keyed by arm, source and the
 * group's base sequence.
 *
 * Held as plain data rather than in a module-level `Map` so the reducer that
 * owns it stays pure and a replay of the same events always produces the same
 * result.
 */
export type BenchmarkTraceBuffers = Readonly<
	Record<string, { chunkCount: number; chunks: readonly (string | undefined)[] }>
>;

export const EMPTY_TRACE_BUFFERS: BenchmarkTraceBuffers = {};

/**
 * Chunks of one payload arrive on consecutive sequence numbers, so the group's
 * base sequence identifies it.
 */
const traceChunkKey = (event: BenchmarkTraceEventData): string =>
	`${event.arm ?? 'none'}:${event.source}:${event.sequence - event.chunkIndex}`;

/**
 * Only the fields this module reads. The real records also carry `arguments`,
 * `result`, `error` bodies, `aggregated_output` and `text`, which are
 * deliberately absent from this type so they cannot be read by accident.
 */
type CodexItem = {
	id?: unknown;
	type?: unknown;
	command?: unknown;
	exit_code?: unknown;
	status?: unknown;
	server?: unknown;
	tool?: unknown;
	/** Fallbacks the producer itself accepts for the tool's name. */
	name?: unknown;
	tool_name?: unknown;
	/** Read for truthiness only, never for content. */
	error?: unknown;
};

type CodexRecord = {
	type?: unknown;
	item?: unknown;
};

const parseCodexRecord = (content: string): CodexRecord | null => {
	try {
		const parsed: unknown = JSON.parse(content);

		return typeof parsed === 'object' && parsed !== null ? (parsed as CodexRecord) : null;
	} catch {
		return null;
	}
};

const readCommandLabel = (item: CodexItem): string =>
	typeof item.command === 'string' ? unwrapShellCommand(item.command) : '';

/**
 * Mirrors the producer's own name resolution, which falls back through three
 * fields. Reading only the first would silently degrade the label to a bare
 * server name for any tool that reports its name differently.
 */
const readMcpLabel = (item: CodexItem): string => {
	const server = typeof item.server === 'string' ? item.server : 'mcp';
	const tool = [item.tool, item.name, item.tool_name].find(
		(candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0,
	);

	return tool ? `${server}.${tool}` : server;
};

const toInvocationDelta = (record: CodexRecord): BenchmarkInvocationDelta | null => {
	const phase =
		record.type === 'item.started'
			? 'started'
			: record.type === 'item.completed'
				? 'completed'
				: null;

	if (phase === null || typeof record.item !== 'object' || record.item === null) {
		return null;
	}

	const item = record.item as CodexItem;

	if (typeof item.id !== 'string' || item.id.length === 0) {
		return null;
	}

	const exitCode = typeof item.exit_code === 'number' ? item.exit_code : null;
	const statusFailed = item.status === 'failed';

	if (item.type === 'command_execution') {
		const label = readCommandLabel(item);

		return {
			phase,
			invocationId: item.id,
			kind: 'command',
			surface: TWG_COMMAND_PATTERN.test(label) ? 'twg' : 'shell',
			label,
			failed: phase === 'completed' && (statusFailed || (exitCode !== null && exitCode !== 0)),
			exitCode,
		};
	}

	// Both spellings are live: the producer counts either towards its MCP
	// integrity checks, so recognising only one would leave a violation on
	// screen with no invocation to account for it.
	if (item.type === 'mcp_tool_call' || item.type === 'mcp_call') {
		return {
			phase,
			invocationId: item.id,
			kind: 'mcp',
			surface: 'mcp',
			label: readMcpLabel(item),
			// Tested for content rather than for presence: the field is emitted as
			// `null` on a successful call, so a presence check would report every
			// MCP call as failed. Its body is never read, only its truthiness.
			failed: phase === 'completed' && (statusFailed || Boolean(item.error)),
			exitCode: null,
		};
	}

	return null;
};

/**
 * Folds one `trace` event into the chunk buffers, returning what it revealed.
 *
 * Multi-chunk groups are held until every slot is filled. A group that never
 * completes is simply never read, which is the correct behaviour: the producer
 * stops tracing on overflow, so an incomplete group means the payload was
 * dropped upstream rather than that something went wrong here.
 */
export const readTraceEvent = (
	buffers: BenchmarkTraceBuffers,
	event: BenchmarkTraceEventData,
): { buffers: BenchmarkTraceBuffers; outcome: BenchmarkTraceOutcome } => {
	if (event.content === CAPTURE_TRUNCATED_CONTENT) {
		return { buffers, outcome: { kind: 'captureIncomplete', reason: 'limit' } };
	}

	if (event.content === REDACTION_FAILED_CONTENT || event.redactionFailed) {
		return { buffers, outcome: { kind: 'captureIncomplete', reason: 'redactionFailed' } };
	}

	if (event.source !== 'codex_jsonl') {
		return { buffers, outcome: { kind: 'ignored' } };
	}

	if (event.chunkCount <= 1) {
		const record = parseCodexRecord(event.content);
		const delta = record && toInvocationDelta(record);

		return {
			buffers,
			outcome: delta ? { kind: 'invocation', delta } : { kind: 'ignored' },
		};
	}

	const key = traceChunkKey(event);
	const existing = buffers[key];
	const chunks = [...(existing?.chunks ?? new Array<string | undefined>(event.chunkCount))];

	chunks[event.chunkIndex] = event.content;

	if (chunks.some((chunk) => chunk === undefined)) {
		return {
			buffers: { ...buffers, [key]: { chunkCount: event.chunkCount, chunks } },
			outcome: { kind: 'ignored' },
		};
	}

	const { [key]: _completed, ...remaining } = buffers;
	const record = parseCodexRecord(chunks.join(''));
	const delta = record && toInvocationDelta(record);

	return {
		buffers: remaining,
		outcome: delta ? { kind: 'invocation', delta } : { kind: 'ignored' },
	};
};
